import { z } from "zod";
import { TRANSLATION_LOCALES, nameTranslationsField, type NameTranslations, type TranslationLocale } from "./competition/common";
import { searchParam } from "./pagination";
import { makeSortOrderField, sameTranslations, settingsPaginationQuery } from "./organization-types";

/**
 * Access codes — what an accreditation card grants, as configurable rows
 * (`access_codes`) rather than Postgres enums, so adding a zone needs no
 * migration and no deploy.
 *
 *   ZONE       venue access tiles printed on the card (HQ, INF, VP, MPC…)
 *   SEAT       seating numbers 1–6
 *   TRANSPORT  T1–T3. No transport = no row; there is no "None" code.
 *   MEAL       meal sessions B / L / D
 *
 * The database CHECK on `access_codes.kind` is generated from this list
 * (`inList()`), so the API's validator and the DDL can never disagree. The
 * ORDER of this list is the display order everywhere (lists, the access
 * rules table, the stats strip).
 */
export const ACCESS_CODE_KINDS = ["ZONE", "SEAT", "TRANSPORT", "MEAL"] as const;
export type AccessCodeKind = (typeof ACCESS_CODE_KINDS)[number];

/** An access set (a zone access rule, a card's own access) may name at most this many codes. */
export const ACCESS_DEFAULTS_MAX = 100;
/** …and at most this many of kind TRANSPORT: a card carries one transport entitlement. */
export const ACCESS_DEFAULTS_TRANSPORT_MAX = 1;

export const ACCESS_CODE_SORT_KEYS = ["kind", "sort_order", "code", "name", "created_at"] as const;
export type AccessCodeSortKey = (typeof ACCESS_CODE_SORT_KEYS)[number];

export const listAccessCodesQuery = settingsPaginationQuery.extend({
	kind: z.enum(ACCESS_CODE_KINDS).optional(),
	/** Matches `code` or `name`, case-insensitively. */
	search: searchParam.optional(),
	/** Default: kind (in `ACCESS_CODE_KINDS` order), then sort_order, then code. */
	sort: z.enum(ACCESS_CODE_SORT_KEYS).optional(),
	dir: z.enum(["asc", "desc"]).optional(),
});
export type ListAccessCodesQuery = z.infer<typeof listAccessCodesQuery>;

export const accessCodeOptionsQuery = z.object({
	kind: z.enum(ACCESS_CODE_KINDS).optional(),
});
export type AccessCodeOptionsQuery = z.infer<typeof accessCodeOptionsQuery>;

const descriptionText = z.string().trim().min(1).max(500);

/** Like `nameTranslationsField` (fixed locale keys, `.strict()`, no `en`), for the longer description. */
export const descriptionTranslationsField = z
	.object(
		Object.fromEntries(TRANSLATION_LOCALES.map((code) => [code, descriptionText.optional()])) as Record<
			TranslationLocale,
			z.ZodOptional<typeof descriptionText>
		>,
	)
	.strict();
export type DescriptionTranslations = z.infer<typeof descriptionTranslationsField>;

/** One row of `GET /access-codes` (and the detail / write responses). */
export interface AccessCodeListItem {
	access_code_id: string;
	kind: AccessCodeKind;
	/** Printed on the card, in the case typed. Unique within its kind. */
	code: string;
	name: string;
	description: string | null;
	name_translations: NameTranslations;
	description_translations: DescriptionTranslations;
	sort_order: number;
	is_active: boolean;
	/** Zone access rules naming this code. A code in use cannot be deleted. */
	rules_count: number;
	created_at: string;
	updated_at: string;
}

/** `GET /access-codes/options?kind=` — active rows, any signed-in role. */
export interface AccessCodeOption {
	access_code_id: string;
	kind: AccessCodeKind;
	code: string;
	name: string;
	name_translations: NameTranslations;
	sort_order: number;
}

/** `GET /access-codes/stats` — the stats strip, one aggregate. `by_kind` follows `ACCESS_CODE_KINDS` order. */
export interface AccessCodeStats {
	total: number;
	active: number;
	inactive: number;
	by_kind: Array<{ kind: AccessCodeKind; total: number; active: number }>;
}

// ── Write requests ──────────────────────────────────────────────────────────

export interface AccessCodeFormMessages {
	codeRequired: string;
	/** Letters, digits, `-` and `_` only. */
	codeFormat: string;
	nameRequired: string;
	sortOrderInvalid: string;
}

export const enAccessCodeFormMessages: AccessCodeFormMessages = {
	codeRequired: "Enter a code",
	codeFormat: "Letters, numbers, - and _ only",
	nameRequired: "Enter a name",
	sortOrderInvalid: "Enter a whole number, 0 or more",
};

/**
 * HQ, VP, 1, T2, B — trimmed FIRST (bug 5), then checked, and kept in the case
 * typed: it prints on the card. Unique per kind in the database.
 */
export const makeAccessCodeCodeField = (m: AccessCodeFormMessages = enAccessCodeFormMessages) =>
	z.string().trim().min(1, m.codeRequired).max(20).regex(/^[A-Za-z0-9_-]+$/, m.codeFormat);
export const accessCodeCodeField = makeAccessCodeCodeField();

const accessCodeFields = (m: AccessCodeFormMessages) => ({
	kind: z.enum(ACCESS_CODE_KINDS),
	code: makeAccessCodeCodeField(m),
	name: z.string().trim().min(1, m.nameRequired).max(200),
	/** `""` = none. */
	description: z.string().trim().max(500),
	name_translations: nameTranslationsField,
	description_translations: descriptionTranslationsField,
	sort_order: makeSortOrderField(m.sortOrderInvalid),
	is_active: z.boolean(),
});

export function makeAccessCodeForm(m: AccessCodeFormMessages = enAccessCodeFormMessages) {
	return z.object(accessCodeFields(m));
}
export const accessCodeForm = makeAccessCodeForm();
export type AccessCodeForm = z.infer<typeof accessCodeForm>;

export interface CreateAccessCodeBody {
	kind: AccessCodeKind;
	code: string;
	name: string;
	description?: string;
	name_translations?: NameTranslations;
	description_translations?: DescriptionTranslations;
	sort_order?: number;
	is_active?: boolean;
}

/** `null` clears `description`; omitted leaves a field alone. */
export interface UpdateAccessCodeBody {
	kind?: AccessCodeKind;
	code?: string;
	name?: string;
	description?: string | null;
	name_translations?: NameTranslations;
	description_translations?: DescriptionTranslations;
	sort_order?: number;
	is_active?: boolean;
	/** Headline for this change in the Activity feed. */
	reason?: string;
}

export const toCreateAccessCodeBody = (f: AccessCodeForm): CreateAccessCodeBody => {
	const { description, ...rest } = f;
	return { ...rest, ...(description ? { description } : {}) };
};

/** Only what actually changed, so the audit row's `changed_fields` names the edit. */
export function toUpdateAccessCodeBody(f: AccessCodeForm, before: AccessCodeListItem): UpdateAccessCodeBody {
	const body: UpdateAccessCodeBody = {};
	if (f.kind !== before.kind) body.kind = f.kind;
	if (f.code !== before.code) body.code = f.code;
	if (f.name !== before.name) body.name = f.name;
	const description = f.description === "" ? null : f.description;
	if (description !== before.description) body.description = description;
	if (!sameTranslations(f.name_translations, before.name_translations)) body.name_translations = f.name_translations;
	if (!sameTranslations(f.description_translations, before.description_translations)) body.description_translations = f.description_translations;
	if (f.sort_order !== before.sort_order) body.sort_order = f.sort_order;
	if (f.is_active !== before.is_active) body.is_active = f.is_active;
	return body;
}
